import React, { useEffect, useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesDark from '@amcharts/amcharts4/themes/dark';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

const AnimatedLineChart = () => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    // Apply themes
    am4core.useTheme(am4themesDark);
    am4core.useTheme(am4themesAnimated);

    // Create chart
    const chart = am4core.create(chartRef.current, am4charts.XYChart);
    chart.hiddenState.properties.opacity = 0;
    chart.padding(0, 0, 0, 0);
    chart.zoomOutButton.disabled = true;

    // Generate initial data
    let data = [];
    let visits = 10;

    for (let i = 0; i <= 30; i++) {
      visits -= Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 10);
      data.push({
        date: new Date().setSeconds(i - 30),
        value: visits
      });
    }

    chart.data = data;

    // Create axes
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.grid.template.location = 0;
    dateAxis.renderer.minGridDistance = 30;
    dateAxis.dateFormats.setKey("second", "ss");
    dateAxis.periodChangeDateFormats.setKey("second", "[bold]h:mm a");
    dateAxis.periodChangeDateFormats.setKey("minute", "[bold]h:mm a");
    dateAxis.periodChangeDateFormats.setKey("hour", "[bold]h:mm a");
    dateAxis.renderer.inside = true;
    dateAxis.renderer.axisFills.template.disabled = true;
    dateAxis.renderer.ticks.template.disabled = true;

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.tooltip.disabled = true;
    valueAxis.interpolationDuration = 500;
    valueAxis.rangeChangeDuration = 500;
    valueAxis.renderer.inside = true;
    valueAxis.renderer.minLabelPosition = 0.05;
    valueAxis.renderer.maxLabelPosition = 0.95;
    valueAxis.renderer.axisFills.template.disabled = true;
    valueAxis.renderer.ticks.template.disabled = true;

    // Create series
    const series = chart.series.push(new am4charts.LineSeries());
    series.dataFields.dateX = "date";
    series.dataFields.valueY = "value";
    series.interpolationDuration = 500;
    series.defaultState.transitionDuration = 0;
    series.tensionX = 0.8;

    // Initial zoom
    chart.events.on("datavalidated", function() {
      dateAxis.zoom({
        start: 1 / 15,
        end: 1.2
      }, false, true);
    });

    dateAxis.interpolationDuration = 500;
    dateAxis.rangeChangeDuration = 500;

    // Add live data
    let interval;
    const startInterval = () => {
      interval = setInterval(() => {
        visits = visits + Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 5);
        const lastDataItem = series.dataItems.getIndex(series.dataItems.length - 1);
        chart.addData({
          date: new Date(lastDataItem.dateX.getTime() + 1000),
          value: visits
        }, 1);
      }, 1000);
    };

    startInterval();

    // Gradient fill
    series.fillOpacity = 1;
    const gradient = new am4core.LinearGradient();
    gradient.addColor(chart.colors.getIndex(0), 0.2);
    gradient.addColor(chart.colors.getIndex(0), 0);
    series.fill = gradient;

    // Label opacity adapter
    dateAxis.renderer.labels.template.adapter.add("fillOpacity", function(fillOpacity, target) {
      const dataItem = target.dataItem;
      return dataItem.position;
    });

    dateAxis.events.on("validated", function() {
      am4core.iter.each(dateAxis.renderer.labels.iterator(), function(label) {
        label.fillOpacity = label.fillOpacity;
      });
    });

    // Label rotation adapter
    dateAxis.renderer.labels.template.adapter.add("rotation", function(rotation, target) {
      const dataItem = target.dataItem;
      if (dataItem.date && dataItem.date.getTime() == am4core.time.round(new Date(dataItem.date.getTime()), "minute").getTime()) {
        target.verticalCenter = "middle";
        target.horizontalCenter = "left";
        return -90;
      } else {
        target.verticalCenter = "bottom";
        target.horizontalCenter = "middle";
        return 0;
      }
    });

    // Bullet at front
    const bullet = series.createChild(am4charts.CircleBullet);
    bullet.circle.radius = 5;
    bullet.fillOpacity = 1;
    bullet.fill = chart.colors.getIndex(0);
    bullet.isMeasured = false;

    series.events.on("validated", function() {
      bullet.moveTo(series.dataItems.last.point);
      bullet.validatePosition();
    });

    // Store chart instance for cleanup
    chartInstance.current = chart;

    // Cleanup function
    return () => {
      if (interval) clearInterval(interval);
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, []);

  return (
    <div 
      ref={chartRef} 
      style={{ 
        width: '100%', 
        height: '300px',
        backgroundColor: 'transparent'
      }}
    ></div>
  );
};

export default AnimatedLineChart;